import { isEmpty, orderBy } from "lodash";
import moment from "moment";
import type { PayloadAction } from "@reduxjs/toolkit";
import { categorizeByDate, sortDates } from "src/helpers/utils";
import type {
  ActivityLogEntry,
  ActivityLogState,
  CustomerMessage,
  JobData,
  JobDetailsState,
  ResidentCaused,
  SlideOutState,
  WatcherInfo,
} from "src/types/jobs";

interface ReadOrUnreadPayload {
  id: number;
  messageAction: "mark-as-read" | "mark-as-unread";
  date: string;
  activeThreadId: string;
}

interface UpdateReduxMessagePayload {
  data: Record<string, unknown>;
  date: string;
  actionType: "update" | "add";
  tempId?: string;
}

export interface PusherMessagePayload {
  data: {
    customer_chat_group_id: number;
    chat: CustomerMessage;

    unread_chat_count: number;
    unread_chat_ids: number[];
    service_request_id: number;
    total_unread_chat_count: number;

    job_ids: number[]
  };
  event: string;
}

const reducers = {
  openJobDetailsSlideOut: (
    state: JobDetailsState,
    action: PayloadAction<SlideOutState>,
  ) => ({
    ...state,
    slideOut: action.payload,
    // Re-arm the loading gate on every open (not just the first) so NavTable/ActivityLog
    // stay hidden until fetchJobData resolves, instead of mounting early with stale
    // isLoading === false left over from the previously viewed job.
    isLoading: true,
  }),
  clearSlideOut: (state: JobDetailsState) => ({
    ...state,
    slideOut: {
      openedJobDetailsId: "",
      type: "",
      jobId: "",
      srId: "",
      activeTab: "",
      hasInitialActivityLogApiTriggered: false,
      needToRefetch: false,
    },
    data: {} as JobDetailsState["data"],
    activityLog: {} as ActivityLogState,
    flagRollup: null,
    isFlagRollupLoading: false,
  }),
  updateSlideOut: (
    state: JobDetailsState,
    action: PayloadAction<Partial<SlideOutState>>,
  ) => ({
    ...state,
    slideOut: {
      ...state.slideOut,
      ...action.payload,
    },
  }),
  clearJobDetails: (state: JobDetailsState) => ({
    ...state,
    data: {} as JobDetailsState["data"],
    activityLog: {} as ActivityLogState,
    messages: {} as JobDetailsState["messages"],
    flagRollup: null,
    isFlagRollupLoading: false,
  }),
  updateSlideOutData: (
    state: JobDetailsState,
    action: PayloadAction<Record<string, unknown>>,
  ) => ({
    ...state,
    data: {
      ...state.data,
      ...action.payload,
    },
  }),

  updateActivityLog: (
    state: JobDetailsState,
    action: PayloadAction<{ data: ActivityLogEntry }>,
  ) => {
    const activityLog = state.activityLog;
    const activityLogData = activityLog?.data || [];
    if (
      action?.payload?.data?.event === "job.tenant.reached" &&
      activityLogData?.[0]?.event === "job.tenant.reached" &&
      !isEmpty(action?.payload?.data?.event_attributes)
    ) {
      const updatedLog = {
        ...activityLogData?.[0],
        created_at: action?.payload?.data?.created_at,
        event_attributes: [
          action?.payload?.data?.event_attributes,
          ...(Array.isArray(activityLogData?.[0]?.event_attributes)
            ? activityLogData[0].event_attributes
            : []),
        ],
      };
      activityLog.data = [
        updatedLog,
        ...(activityLogData || []).slice(1),
      ] as ActivityLogState["data"];
    } else {
      const sameActivityLogIndex = (activityLogData || [])?.findIndex(
        (item) => item?.activity_uuid === action?.payload?.data?.activity_uuid,
      );
      if (sameActivityLogIndex !== -1) {
        activityLogData[sameActivityLogIndex] = {
          ...(action?.payload?.data || {}),
        };
      } else {
        activityLog.data = [
          action.payload?.data || {},
          ...(activityLogData || []),
        ];
      }
    }
  },

  updateMessageToReadOrUnread: (
    state: JobDetailsState,
    action: PayloadAction<ReadOrUnreadPayload>,
  ) => {
    const { id, messageAction, date, activeThreadId } = action.payload;
    const messages = state.messages;
    const messagesData = (messages?.data || {}) as Record<
      string,
      Record<string, unknown>
    >;
    const newDataList = { ...messagesData };
    newDataList[date] = {
      ...(newDataList[date] || {}),
      content: (
        newDataList?.[date]?.content as Array<Record<string, unknown>>
      )?.map((item) => {
        if (item.message_id === id) {
          return {
            ...item,
            read_at: messageAction === "mark-as-read" ? moment().toISOString() : null,
          };
        }
        return item;
      }),
    };
    if (messageAction === "mark-as-read") {
      (messages as Record<string, unknown>).unread_chat_ids = (
        (messages as Record<string, unknown>)?.unread_chat_ids as number[]
      )?.filter((item) => item !== id);
      (messages as Record<string, unknown>).threads = Array.isArray(
        (messages as Record<string, unknown>)?.threads,
      )
        ? (
            (messages as Record<string, unknown>)?.threads as Array<
              Record<string, unknown>
            >
          )?.map((item) => {
            if (item?.customer_chat_group_id === activeThreadId) {
              return {
                ...item,
                unread_chat_count:
                  ((item?.unread_chat_count as number) || 0) - 1,
              };
            }
            return item;
          })
        : [];
    } else {
      (messages as Record<string, unknown>).unread_chat_ids = [
        ...(((messages as Record<string, unknown>)
          ?.unread_chat_ids as string[]) || []),
        id,
      ];
      (messages as Record<string, unknown>).threads = Array.isArray(
        (messages as Record<string, unknown>)?.threads,
      )
        ? (
            (messages as Record<string, unknown>)?.threads as Array<
              Record<string, unknown>
            >
          )?.map((item) => {
            if (item?.customer_chat_group_id === activeThreadId) {
              return {
                ...item,
                unread_chat_count:
                  ((item?.unread_chat_count as number) || 0) + 1,
              };
            }
            return item;
          })
        : [];
    }
    (messages as Record<string, unknown>).data = newDataList;
  },

  clearMessages: (state: JobDetailsState) => {
    state.messages = {
      ...(state.messages || {}),
      data: {},
      links: {},
      meta: undefined,
    } as JobDetailsState["messages"];
  },
  updateMessage: (
    state: JobDetailsState,
    action: PayloadAction<Record<string, unknown>>,
  ) => {
    state.messages = {
      ...(state.messages || {}),
      ...action.payload,
    } as JobDetailsState["messages"];
  },

  updateReduxMessageState: (
    state: JobDetailsState,
    action: PayloadAction<UpdateReduxMessagePayload>,
  ) => {
    const { data, date, actionType, tempId } = action.payload;
    const messagesData = (state?.messages as Record<string, unknown>)?.data as
      | Record<string, Record<string, unknown>>
      | undefined;
    const newDataList: Record<string, Record<string, unknown>> = {
      Today: { value: "Today" },
      ...(messagesData || {}),
    };

    newDataList[date] = {
      ...(newDataList?.[date] || {}),
      content:
        actionType === "update"
          ? (
              (newDataList?.[date]?.content as Array<
                Record<string, unknown>
              >) || []
            ).map((item) =>
              item.message_id === tempId ? { ...item, ...data } : item,
            )
          : [
              data,
              ...((newDataList?.[date]?.content as Array<
                Record<string, unknown>
              >) || []),
            ],
    };
    (state.messages as Record<string, unknown>).data = newDataList;
  },
  updateMessageOnPusher: (
    state: JobDetailsState,
    action: PayloadAction<PusherMessagePayload>,
  ) => {
    const { data, event } = action.payload;
    const messages = state.messages as Record<string, unknown>;
    const { slideOut } = state;
    const stateData = state.data;
    if (event === "customer.chat.created") {
      if (
        data?.customer_chat_group_id ===
          (messages as Record<string, unknown>)?.activeThreadId &&
        slideOut?.activeTab === "message"
      ) {
        const previousMessages = {
          ...((messages?.data as Record<string, Record<string, unknown>>) ||
            {}),
        };
        const timezone = (stateData?.timezone as string) || "";

        const formattedData = categorizeByDate([data?.chat], timezone);
        const formattedDateKeys = Object.keys(formattedData || {})?.[0];
        if (formattedDateKeys) {
          if (previousMessages?.[formattedDateKeys]) {
            // find same message index
            const sameMessageIndex = (
              (previousMessages?.[formattedDateKeys]?.content as Array<
                Record<string, unknown>
              >) || []
            )?.findIndex((item) => item?.message_id === data?.chat?.message_id);
            // if index found then update message in that index other wise push and add to array
            if (sameMessageIndex !== -1) {
              const contentArray = previousMessages[formattedDateKeys]
                .content as Array<Record<string, unknown>>;
              const currentMessage = contentArray[sameMessageIndex];
              const { attachments, ...chatDetails } = data?.chat || {};
              contentArray[sameMessageIndex] = {
                ...{ ...(currentMessage || {}), ...(chatDetails || {}) },
              };
              // prevent attachments from pusher to fully replace
              // existing attachments and update them individually by spreading.
              const typedAttachments = attachments as
                | Array<Record<string, unknown>>
                | undefined;
              const currentAttachments = currentMessage.attachments as
                | Array<Record<string, unknown>>
                | undefined;
              if (
                !isEmpty(typedAttachments) &&
                !isEmpty(currentAttachments) &&
                Array.isArray(typedAttachments) &&
                Array.isArray(currentAttachments)
              ) {
                contentArray[sameMessageIndex].attachments =
                  currentAttachments?.map((attachment) => {
                    const chatAttachment = typedAttachments?.find(
                      (_attachment) =>
                        _attachment.attachment_id === attachment.attachment_id,
                    );

                    return {
                      ...attachment,
                      attachment_url:
                        chatAttachment?.attachment_url ||
                        attachment?.attachment_url,
                      attachment_optimized_url:
                        chatAttachment?.attachment_optimized_url ||
                        attachment?.attachment_optimized_url,
                      thumbnail_url:
                        chatAttachment?.thumbnail_url ||
                        attachment?.thumbnail_url,
                    };
                  });
              }
            } else {
              const newMessages = [
                data?.chat,
                ...((previousMessages?.[formattedDateKeys]?.content as Array<
                  Record<string, unknown>
                >) || []),
              ];
              // Sorting the messages array in ascending order based on the message created at time
              const sortedNewMessages = orderBy(
                newMessages,
                (message) =>
                  moment(
                    (message as Record<string, unknown>)?.created_at as string,
                    "YYYY-MM-DD HH:mm:ss",
                  ).format("YYYY-MM-DD HH:mm:ss"),
                ["desc"],
              );
              (
                previousMessages[formattedDateKeys] as Record<string, unknown>
              ).content = sortedNewMessages;
            }
          } else {
            previousMessages[formattedDateKeys] = {
              value: formattedDateKeys,
              content: [data?.chat],
            };
          }
          // Sort date keys in ascending order and re arrange the customer chat
          const sortedMessagesArray = !isEmpty(previousMessages)
            ? sortDates(Object.keys(previousMessages || {})).reduce(
                (
                  acc: Record<string, Record<string, unknown>>,
                  currentValue: string,
                ) => {
                  acc[currentValue] = previousMessages[currentValue];
                  return acc;
                },
                {},
              )
            : {};
          (messages as Record<string, unknown>).data = sortedMessagesArray;
        }
      }
      (messages as Record<string, unknown>).threads = Array.isArray(
        (messages as Record<string, unknown>)?.threads,
      )
        ? (
            (messages as Record<string, unknown>).threads as Array<
              Record<string, unknown>
            >
          )?.map((thread) => {
            if (
              thread?.customer_chat_group_id === data?.customer_chat_group_id
            ) {
              return {
                ...thread,
                last_message_at: data?.chat?.created_at,
              };
            }
            return thread;
          })
        : [];
    }
    if (
      event === "customer.chat.unread" &&
      !isEmpty(slideOut?.openedJobDetailsId)
    ) {
      if (
        data?.customer_chat_group_id ===
        (messages as Record<string, unknown>).activeThreadId
      ) {
        (messages as Record<string, unknown>).threads = Array.isArray(
          (messages as Record<string, unknown>)?.threads,
        )
          ? (
              (messages as Record<string, unknown>).threads as Array<
                Record<string, unknown>
              >
            )?.map((thread) => {
              if (
                thread?.customer_chat_group_id === data?.customer_chat_group_id
              ) {
                return {
                  ...thread,
                  ...data,
                };
              }
              return thread;
            })
          : [];

        if (
          data?.customer_chat_group_id ===
          (messages as Record<string, unknown>).activeThreadId
        ) {
          (messages as Record<string, unknown>).unread_chat_ids =
            data?.unread_chat_ids;
        }
      } else {
        // to remove the unread items icon from threads that are not active
        const hasThread = (
          (messages as Record<string, unknown>)?.threads as Array<
            Record<string, unknown>
          >
        )?.some(
          (thread) =>
            thread.customer_chat_group_id === data?.customer_chat_group_id,
        );
        if (hasThread) {
          (messages as Record<string, unknown>).threads = Array.isArray(
            (messages as Record<string, unknown>)?.threads,
          )
            ? (
                (messages as Record<string, unknown>)?.threads as Array<
                  Record<string, unknown>
                >
              )?.map((thread) => {
                if (
                  thread?.customer_chat_group_id ===
                  data?.customer_chat_group_id
                ) {
                  return {
                    ...thread,
                    ...data,
                  };
                }
                return thread;
              })
            : [];
        } else {
          (messages as Record<string, unknown>).shouldRefetchGroups = true;
        }
      }
      if (stateData?.customer_message) {
        (
          stateData.customer_message as Record<string, unknown>
        ).total_unread_chat_count = data?.total_unread_chat_count;
      }
    }
    if (
      event === "customer.chat.unread-count" &&
      !isEmpty(slideOut?.openedJobDetailsId)
    ) {
      if (
        stateData?.service_request_id === data?.service_request_id &&
        stateData?.customer_message
      ) {
        stateData.customer_message.total_unread_chat_count =
          data?.total_unread_chat_count ?? 0;
      }
    }
  },

  updateWatcherList: (
    state: JobDetailsState,
    action: PayloadAction<WatcherInfo[]>,
  ) => {
    state.data.watchers = action.payload;
  },

  clearMessageConfig: (state: JobDetailsState) => {
    state.messages = {};
  },

  updatePropertyEscalation: (
    state: JobDetailsState,
    action: PayloadAction<{ property_escalation: JobData['property_escalation'] }>,
  ) => ({
    ...state,
    data: {
      ...state.data,
      property_escalation: action.payload.property_escalation,
    },
  }),

  updateResidentCausedFromPusher: (
    state: JobDetailsState,
    action: PayloadAction<{ jobId: number; isRemoved: boolean; entry?: ResidentCaused }>,
  ) => {
    const { jobId, isRemoved, entry } = action.payload;
    const current = state.data.resident_caused ?? [];

    if (isRemoved) {
      const updated = current.filter((rc) => rc.job_id !== jobId);
      state.data.resident_caused = updated.length > 0 ? updated : null;
    } else if (entry) {
      const index = current.findIndex((rc) => rc.job_id === jobId);
      if (index !== -1) {
        const updated = [...current];
        updated[index] = entry;
        state.data.resident_caused = updated;
      } else {
        state.data.resident_caused = [...current, entry];
      }
    }
  },
};

export default reducers;
